/**
 * useFDACredibility - Custom Hook for FDA AI Credibility Lens
 * ============================================================================
 * DC-930: Hook for fetching and managing FDA Credibility assessment data
 *
 * Features:
 * - Fetch latest FDA credibility assessment
 * - Run new assessment with model risk configuration
 * - Loading and error states
 * - Auto-refresh capability
 *
 * Pattern: Follows useComplianceLens.ts (DC-821)
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react';

// ============================================================================
// Types
// ============================================================================

export type FDACredibilityLevel = 'high' | 'adequate' | 'low' | 'insufficient';
export type ModelInfluence = 'low' | 'medium' | 'high';
export type DecisionConsequence = 'low' | 'medium' | 'high' | 'critical';
export type CalculatedRiskLevel = 'low' | 'medium' | 'high';
export type FindingSeverity = 'critical' | 'major' | 'minor' | 'observation';

export interface GapsSummary {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  total: number;
}

export interface ModelRiskContext {
  influence: ModelInfluence;
  consequence: DecisionConsequence;
  calculated_risk: CalculatedRiskLevel;
  required_evidence_level: 'basic' | 'standard' | 'comprehensive';
}

/**
 * The declared Context of Use (S1, DC-1684) — the frame the verdict is judged
 * against, sourced from Contextual Validity. `declared: false` means no COU was
 * declared (UI should prompt to declare, not show an abstract verdict).
 */
export interface ContextOfUseSummary {
  declared: boolean;
  problem_statement?: string;
  intended_use?: string;
  problem_type?: string;
  sensitivity_level?: string;
  regulatory_context?: string;
}

export interface FDACredibilityResult {
  id: string;
  journey_id: string;
  asset_id?: string;
  overall_score: number;
  credibility_level: FDACredibilityLevel;
  is_fit_for_use: boolean;
  relevance_score?: number;
  reliability_score?: number;
  model_risk?: ModelRiskContext;
  context_of_use?: ContextOfUseSummary;
  /** Nested step sub-scores for the dimension breakdown (from the lens result). */
  fit_for_use?: {
    relevance?: {
      context_of_use?: { score: number };
      representativeness?: { score: number };
      sample_size?: { score: number };
    };
    reliability?: {
      accuracy?: { score: number };
      completeness?: { score: number };
      traceability?: { score: number };
    };
  };
  gaps_summary: GapsSummary;
  gaps_count?: number;
  recommendations_count?: number;
  weights_used: {
    relevance: number;
    reliability: number;
  };
  assessed_at: string;
  assessor_version: string;
}

export interface FDACredibilityResponse {
  success: boolean;
  result?: FDACredibilityResult;
  meta?: {
    credibilityLevelDescription: string;
    credibilityLevelColor: string;
    hasModelRisk: boolean;
  };
  error?: string;
}

export interface AssessmentConfig {
  modelInfluence?: ModelInfluence;
  decisionConsequence?: DecisionConsequence;
  organizationId?: string;
}

export interface UseFDACredibilityOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Polling interval in ms (0 to disable) */
  pollInterval?: number;
}

export interface UseFDACredibilityReturn {
  /** Latest FDA credibility result */
  result: FDACredibilityResult | null;
  /** Whether data is loading */
  loading: boolean;
  /** Error message if any */
  error: Error | null;
  /** Whether an assessment is currently running */
  isRunning: boolean;
  /** Meta information from API */
  meta: FDACredibilityResponse['meta'] | null;
  /** Fetch the latest result */
  fetchResult: () => Promise<void>;
  /** Run a new assessment */
  runAssessment: (config?: AssessmentConfig) => Promise<FDACredibilityResult | null>;
  /** Clear error state */
  clearError: () => void;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get human-readable description for credibility level
 */
export function getCredibilityLevelDescription(level: FDACredibilityLevel): string {
  switch (level) {
    case 'high':
      return 'Data meets FDA credibility standards. Suitable for regulatory decision-making.';
    case 'adequate':
      return 'Data meets minimum FDA credibility standards with documented limitations.';
    case 'low':
      return 'Data has significant credibility gaps. Remediation required.';
    case 'insufficient':
      return 'Data does not meet FDA credibility standards. Not suitable for regulatory use.';
  }
}

/**
 * Get color for credibility level
 */
export function getCredibilityLevelColor(level: FDACredibilityLevel): string {
  switch (level) {
    case 'high':
      return 'var(--tri-good)';
    case 'adequate':
      return 'var(--tri-soft)';
    case 'low':
      return 'var(--tri-terra)';
    case 'insufficient':
      return 'var(--tri-hard)';
  }
}

/**
 * Get color for risk level
 */
export function getRiskLevelColor(level: CalculatedRiskLevel): string {
  switch (level) {
    case 'low':
      return 'var(--tri-good)';
    case 'medium':
      return 'var(--tri-soft)';
    case 'high':
      return 'var(--tri-hard)';
  }
}

/**
 * Get color for severity
 */
export function getSeverityColor(severity: FindingSeverity): string {
  switch (severity) {
    case 'critical':
      return 'var(--tri-hard)';
    case 'major':
      return 'var(--tri-terra)';
    case 'minor':
      return 'var(--tri-soft)';
    case 'observation':
      return 'var(--tri-neutral)';
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useFDACredibility(
  journeyId: string,
  options: UseFDACredibilityOptions = {}
): UseFDACredibilityReturn {
  const { autoFetch = true, pollInterval = 0 } = options;

  const [result, setResult] = useState<FDACredibilityResult | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<Error | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [meta, setMeta] = useState<FDACredibilityResponse['meta'] | null>(null);

  /**
   * Fetch the latest FDA credibility assessment result
   */
  const fetchResult = useCallback(async () => {
    if (!journeyId) {
      setError(new Error('Journey ID is required'));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/v1/lenses/fda-credibility/${journeyId}`);

      if (response.status === 404) {
        // No assessment yet - this is not an error
        setResult(null);
        setMeta(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch FDA credibility assessment: ${response.statusText}`);
      }

      const data: FDACredibilityResponse = await response.json();

      if (data.success && data.result) {
        setResult(data.result);
        setMeta(data.meta || null);
      } else {
        setResult(null);
        setMeta(null);
      }
    } catch (err) {
      console.error('[useFDACredibility] Fetch error:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  /**
   * Run a new FDA credibility assessment
   */
  const runAssessment = useCallback(
    async (config?: AssessmentConfig): Promise<FDACredibilityResult | null> => {
      if (!journeyId) {
        setError(new Error('Journey ID is required'));
        return null;
      }

      try {
        setIsRunning(true);
        setError(null);

        const response = await fetch(`/api/v1/lenses/fda-credibility/${journeyId}/assess`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            modelInfluence: config?.modelInfluence,
            decisionConsequence: config?.decisionConsequence,
            organizationId: config?.organizationId,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to run FDA credibility assessment: ${response.statusText}`);
        }

        const data: FDACredibilityResponse = await response.json();

        if (data.success && data.result) {
          setResult(data.result);
          setMeta(data.meta || null);
          return data.result;
        }

        return null;
      } catch (err) {
        console.error('[useFDACredibility] Assessment error:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setIsRunning(false);
      }
    },
    [journeyId]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch && journeyId) {
      fetchResult();
    }
  }, [journeyId, autoFetch, fetchResult]);

  // Polling
  useEffect(() => {
    if (pollInterval > 0 && journeyId) {
      const intervalId = setInterval(() => {
        fetchResult();
      }, pollInterval);

      return () => clearInterval(intervalId);
    }
  }, [pollInterval, journeyId, fetchResult]);

  return {
    result,
    loading,
    error,
    isRunning,
    meta,
    fetchResult,
    runAssessment,
    clearError,
  };
}

export default useFDACredibility;
